import type { Distance, Point } from "@pdfking/math"
import { Abs, Max, Min, Round } from "@pdfking/math"
import { MM } from "@pdfking/quantity"

import type { ResolvedGrow } from "#internal"

import { BoxCorners, GrowLeafBoxed, GrowLeafPadded } from "./GrowLeafShape"

type Segment = [Point<Distance>, Point<Distance>]

/** A grow's die, in the grow's own frame, y up: the `cut` round the pieces
 * as they tile the grow — one loop each, counter-clockwise, the first the
 * outline —, and a `fold` along every crease, where the pieces either side
 * touch. */
export type GrowDie = {
  cut: Point<Distance>[][]
  fold: Segment[]
}

/**
 * The die a grow is cut and creased to, from its tree alone: every piece is
 * a polygon, and the cut is the boundary of their union — an edge two pieces
 * share is no cut —; every crease of the tree is a fold where the pieces
 * either side of it meet. The pieces must tile the grow without overlapping,
 * as the layouts place them.
 */
export const GrowDie = (grow: ResolvedGrow): GrowDie => {
  const eps = Max(grow.size.width, grow.size.height, MM(1)) * 1e-9
  const near = (a: number, b: number) => Abs(a - b) <= eps
  const key = (p: Point<Distance>) => `${Round(p.x / eps / 1e3)},${Round(p.y / eps / 1e3)}`
  const key_of = ([a, b]: Segment) => `${key(a)}>${key(b)}`

  // Every directed edge of every piece, counter-clockwise: its padded box,
  // or its corners where a taper draws its tip in — a tapered flap has no bleed
  const edges: Segment[] = grow.leaves.flatMap(leaf => {
    const points = GrowLeafBoxed(leaf) ? BoxCorners(GrowLeafPadded(leaf)) : leaf.corners
    return points.map((p, i) => [p, points[(i + 1) % points.length]!] as const)
  })

  // An axis-aligned edge split where any collinear edge's end falls on it:
  // two edges that overlap then share their pieces exactly
  const split = (edge: Segment): Segment[] => {
    const [a, b] = edge
    const axis = near(a.y, b.y) ? "x" : near(a.x, b.x) ? "y" : undefined
    if (!axis) return [edge]
    const other = axis === "x" ? "y" : "x"
    const [lo, hi] = [Min(a[axis], b[axis]), Max(a[axis], b[axis])]
    const cuts = edges
      .flatMap(([p, q]) =>
        near(p[other], a[other]) && near(q[other], a[other]) ? [p[axis], q[axis]] : []
      )
      .filter(v => v > lo + eps && v < hi - eps)
      .sort((p, q) => (a[axis] < b[axis] ? p - q : q - p))
    const stops = [a, ...cuts.map(v => ({ ...a, [axis]: v }) as Point<Distance>), b]
    return stops.slice(1).map((q, i) => [stops[i]!, q] as const)
  }
  const pieces = edges.flatMap(split)

  // An edge matched by its reverse is inside: the pieces either side share it
  const reversed = new Set(pieces.map(([a, b]) => key_of([b, a])))
  const boundary = pieces.filter(edge => !reversed.has(key_of(edge)))

  // The boundary chained into loops, the outline first; a run of collinear
  // edges is one
  const from = new Map(boundary.map(edge => [key(edge[0]), edge]))
  const loops: Point<Distance>[][] = []
  const used = new Set<Segment>()
  for (const start of boundary) {
    if (used.has(start)) continue
    const loop: Point<Distance>[] = []
    for (let edge = start; !used.has(edge); edge = from.get(key(edge[1]))!) {
      used.add(edge)
      loop.push(edge[0])
    }
    loops.push(
      loop.filter((p, i) => {
        const [before, after] = [loop.at(i - 1)!, loop[(i + 1) % loop.length]!]
        return (
          Abs((p.x - before.x) * (after.y - p.y) - (p.y - before.y) * (after.x - p.x)) > eps ** 2
        )
      })
    )
  }
  const area = (loop: Point<Distance>[]) =>
    Abs(
      loop.reduce((sum, p, i) => {
        const q = loop[(i + 1) % loop.length]!
        return sum + p.x * q.y - q.x * p.y
      }, 0)
    )
  const cut = loops.sort((p, q) => area(q) - area(p))

  // Each crease where the pieces either side of it touch, within its span,
  // touching runs as one; a joint of no turn — a tab's — is no crease
  const fold: Segment[] = grow.joints.flatMap(joint => {
    if (!joint.angle) return []
    const [along, across] = joint.axis === "x" ? (["y", "x"] as const) : (["x", "y"] as const)
    const size = { x: "width", y: "height" } as const
    // The pieces of a side on the crease: where each runs along it. Along x
    // the layout before is left of the crease, along y above it.
    const side = (leaves: number[], at_end: boolean) =>
      leaves
        .map(i => GrowLeafPadded(grow.leaves[i]!))
        .filter(box =>
          near(
            at_end === (across === "x") ? box[across] + box[size[across]] : box[across],
            joint.at
          )
        )
        .map(box => [box[along], box[along] + box[size[along]]] as const)
    const runs: [number, number][] = []
    for (const b of side(joint.before, true))
      for (const a of side(joint.after, false)) {
        const [lo, hi] = [Max(a[0], b[0], joint.span[0]), Min(a[1], b[1], joint.span[1])]
        if (hi - lo > eps) runs.push([lo, hi])
      }
    runs.sort((p, q) => p[0] - q[0])
    const merged: [number, number][] = []
    for (const [lo, hi] of runs) {
      const last = merged[merged.length - 1]
      if (last && lo <= last[1] + eps) last[1] = Max(last[1], hi)
      else merged.push([lo, hi])
    }
    return merged.map(
      ([lo, hi]) =>
        [
          { [along]: lo, [across]: joint.at } as Point<Distance>,
          { [along]: hi, [across]: joint.at } as Point<Distance>,
        ] as const
    )
  })

  return { cut, fold }
}
